/**
 * 🚀 SPACE NAV - Navigation du Cockpit
 * Sidebar style vaisseau spatial avec modules
 */

import React from 'react';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, Users, Truck, MessageSquare, Heart,
  Award, Shield, TrendingUp, Settings, LogOut, Gamepad2,
  CreditCard, Target, FileText, Bell, BarChart3, Download,
  Megaphone, Gift, UserPlus, Eye, Radio, Mail, Bot
} from 'lucide-react';

// Configuration des modules du vaisseau
export const SPACE_MODULES = [
  {
    id: 'dashboard',
    label: 'Cockpit',
    icon: LayoutDashboard,
    color: 'cyan',
    description: 'Vue d\'ensemble'
  },
  {
    id: 'customers',
    label: 'Équipage',
    icon: Users,
    color: 'green',
    description: 'Gestion clients'
  },
  {
    id: 'delivery',
    label: 'Missions',
    icon: Truck,
    color: 'orange',
    badge: true,
    description: 'Livraisons'
  },
  {
    id: 'messaging',
    label: 'Comms',
    icon: MessageSquare,
    color: 'cyan',
    badge: true,
    description: 'Messagerie'
  },
  {
    id: 'sms',
    label: 'Broadcast',
    icon: Radio,
    color: 'yellow',
    description: 'SMS en masse'
  },
  {
    id: 'vendors',
    label: 'Escadron',
    icon: Shield,
    color: 'orange',
    description: 'Équipe épiciers'
  },
  {
    id: 'credits',
    label: 'Crédits',
    icon: CreditCard,
    color: 'yellow',
    description: 'Gestion crédits'
  },
  {
    id: 'promos',
    label: 'Promos',
    icon: Gift,
    color: 'pink',
    description: 'Promotions'
  },
  {
    id: 'loyalty-cards',
    label: 'Cartes Fidélité',
    icon: Award,
    color: 'purple',
    description: 'Templates cartes'
  },
  {
    id: 'prospects',
    label: 'Radar',
    icon: Target,
    color: 'cyan',
    badge: true,  // Activer le badge pour les nouveaux prospects
    description: 'Prospects'
  },
  {
    id: 'automation',
    label: 'Auto',
    icon: Bot,
    color: 'purple',
    description: 'Automatisation'
  },
  {
    id: 'heartbeats',
    label: 'Heartbeats',
    icon: Heart,
    color: 'pink',
    description: 'Module rencontres'
  },
  {
    id: 'content',
    label: 'Contenu',
    icon: FileText,
    color: 'purple',
    description: 'News, Events, Sondages'
  },
  {
    id: 'analytics',
    label: 'Sensors',
    icon: BarChart3,
    color: 'cyan',
    description: 'Analytiques'
  },
  {
    id: 'visitors',
    label: 'Visiteurs',
    icon: Eye,
    color: 'gray',
    description: 'Sessions visiteurs'
  },
  {
    id: 'export',
    label: 'Export',
    icon: Download,
    color: 'green',
    description: 'Export données'
  },
];

// Composant NavItem
const NavItem = ({ 
  module, 
  isActive, 
  onClick, 
  badge = 0,
  collapsed = false 
}) => {
  const Icon = module.icon;
  
  const colorClasses = {
    cyan: isActive ? 'bg-cyan-500/20 border-cyan-500 text-cyan-400' : 'border-transparent hover:bg-cyan-500/10 hover:border-cyan-500/30 text-gray-400 hover:text-cyan-400',
    orange: isActive ? 'bg-orange-500/20 border-orange-500 text-orange-400' : 'border-transparent hover:bg-orange-500/10 hover:border-orange-500/30 text-gray-400 hover:text-orange-400',
    green: isActive ? 'bg-green-500/20 border-green-500 text-green-400' : 'border-transparent hover:bg-green-500/10 hover:border-green-500/30 text-gray-400 hover:text-green-400',
    pink: isActive ? 'bg-pink-500/20 border-pink-500 text-pink-400' : 'border-transparent hover:bg-pink-500/10 hover:border-pink-500/30 text-gray-400 hover:text-pink-400',
    purple: isActive ? 'bg-purple-500/20 border-purple-500 text-purple-400' : 'border-transparent hover:bg-purple-500/10 hover:border-purple-500/30 text-gray-400 hover:text-purple-400',
    yellow: isActive ? 'bg-yellow-500/20 border-yellow-500 text-yellow-400' : 'border-transparent hover:bg-yellow-500/10 hover:border-yellow-500/30 text-gray-400 hover:text-yellow-400',
    gray: isActive ? 'bg-gray-500/20 border-gray-500 text-gray-300' : 'border-transparent hover:bg-gray-500/10 hover:border-gray-500/30 text-gray-500 hover:text-gray-300',
  };

  return (
    <motion.button
      whileHover={{ x: 4 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`
        relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl
        border-l-2 transition-all duration-200
        ${colorClasses[module.color] || colorClasses.cyan}
      `}
      title={collapsed ? module.label : undefined}
    >
      <Icon className="w-5 h-5 flex-shrink-0" />
      {!collapsed && (
        <>
          <span className="text-sm font-medium flex-1 text-left">{module.label}</span>
          {badge > 0 && (
            <span className="w-5 h-5 flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold">
              {badge > 9 ? '9+' : badge}
            </span>
          )}
        </>
      )}
      {collapsed && badge > 0 && (
        <span className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold">
          {badge > 9 ? '9+' : badge}
        </span>
      )}
    </motion.button>
  );
};

// Sidebar principale
const SpaceNav = ({ 
  activeModule, 
  onModuleChange, 
  badges = {},
  onLogout,
  collapsed = false,
  onToggleCollapse
}) => {
  return (
    <motion.div 
      initial={{ x: -20, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      className={`
        flex flex-col h-full
        backdrop-blur-xl bg-[#0a0a0f]/95
        border-r border-cyan-500/20
        ${collapsed ? 'w-20' : 'w-64'}
        transition-all duration-300
      `}
    >
      {/* Header */}
      <div className="p-4 border-b border-cyan-500/20">
        <div className="flex items-center gap-3">
          <motion.div
            animate={{ 
              boxShadow: [
                '0 0 10px rgba(0, 212, 255, 0.3)',
                '0 0 20px rgba(0, 212, 255, 0.6)',
                '0 0 10px rgba(0, 212, 255, 0.3)'
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center"
          >
            <span className="text-xl">🚀</span>
          </motion.div>
          {!collapsed && (
            <div>
              <h1 className="text-white font-black text-sm">LA PETITE</h1>
              <p className="text-cyan-400 text-xs font-medium">ÉPICERIE</p>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto p-3 space-y-1">
        {SPACE_MODULES.map(module => (
          <NavItem
            key={module.id}
            module={module}
            isActive={activeModule === module.id}
            onClick={() => onModuleChange(module.id)}
            badge={module.badge ? badges[module.id] : 0}
            collapsed={collapsed}
          />
        ))}
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-cyan-500/20 space-y-1">
        <NavItem
          module={{ id: 'settings', label: 'Paramètres', icon: Settings, color: 'gray' }}
          isActive={activeModule === 'settings'}
          onClick={() => onModuleChange('settings')}
          collapsed={collapsed}
        />
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-400 hover:bg-red-500/10 transition-colors"
        >
          <LogOut className="w-5 h-5" />
          {!collapsed && <span className="text-sm font-medium">Déconnexion</span>}
        </button>
      </div>

      {/* Version */}
      {!collapsed && (
        <div className="p-3 text-center border-t border-cyan-500/10">
          <p className="text-gray-600 text-xs">v3.0 SPACE EDITION</p>
          <p className="text-cyan-500/50 text-[10px]">Heartbeats Labs</p>
        </div>
      )}
    </motion.div>
  );
};

export default SpaceNav;
